import type { FullscriptClient } from "@/lib/fullscript/client";
import { FullscriptApiError } from "@/lib/fullscript/client";
import type { FullscriptPatientDemographics } from "@/lib/fullscript/patient-demographics";
import { createLogger } from "@/lib/logger";

const logger = createLogger("fullscript-patient-demographics");

// bound for all PATCHes together, so a slow Fullscript cannot stall the draft save or the link
const PATCH_TIMEOUT_MS = 5000;

type DemographicField = keyof FullscriptPatientDemographics;

type PatchOutcome =
  | { field: DemographicField; isSent: true }
  | { field: DemographicField; isSent: false; status: number | null; errorFields?: string[] };

/**
 * Best-effort: PATCHes each demographic field to the Fullscript patient in its own request.
 * One PATCH per field means a rejected value (a phone Fullscript does not accept) cannot drop
 * the others. The requests run in parallel, so the caller waits for one round trip, not one per
 * field. Never throws. Once the shared timeout fires, the requests still in flight abort.
 *
 * Logs carry field names and HTTP status only. The values are PHI.
 * @returns The fields that Fullscript accepted, for the caller's log.
 */
export async function patchFullscriptDemographics({
  client,
  fullscriptPatientId,
  demographics,
  memberId,
  practiceId,
}: {
  client: FullscriptClient;
  fullscriptPatientId: string;
  demographics: FullscriptPatientDemographics;
  memberId: string | undefined;
  practiceId: string | null;
}): Promise<DemographicField[]> {
  const fields = Object.keys(demographics) as DemographicField[];
  if (!fields.length) return [];

  const signal = AbortSignal.timeout(PATCH_TIMEOUT_MS);
  // Each PATCH sets a different field, so they do not overwrite each other
  const outcomes = await Promise.all(
    fields.map(async (field): Promise<PatchOutcome> => {
      try {
        await client.updatePatient(
          fullscriptPatientId,
          { [field]: demographics[field] },
          { signal },
        );
        return { field, isSent: true };
      } catch (error) {
        const isApiError = error instanceof FullscriptApiError;
        return {
          field,
          isSent: false,
          status: isApiError ? error.status : null,
          errorFields: isApiError ? error.summary?.errorFields : undefined,
        };
      }
    }),
  );
  const sentFields = outcomes.filter((outcome) => outcome.isSent).map(({ field }) => field);
  const failures = outcomes.flatMap((outcome) =>
    outcome.isSent
      ? []
      : [{ field: outcome.field, status: outcome.status, errorFields: outcome.errorFields }],
  );

  if (!failures.length) return sentFields;
  // Out of Sentry: a value Fullscript rejects is rejected again on every draft save, and nothing
  // is lost - the patient can still enter it at Fullscript checkout.
  logger.warn(
    "Fullscript rejected patient demographics",
    { memberId, practiceId, fullscriptPatientId, failures },
    { mirrorToSentry: false },
  );
  return sentFields;
}
